import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Radio,
  RadioGroup,
  Input,
  useToast,
  Progress,
  Card,
  CardBody,
  CardFooter,
  Select,
  useId,
} from '@chakra-ui/react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const API_URL = API_BASE_URL;

function Quiz() {
  const { week, subtopicId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const startQuiz = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/quiz/start`, {
          weekNumber: parseInt(week),
          subtopicId,
        });
        setQuestions(response.data.questions);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to start quiz',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    startQuiz();
  }, [week, subtopicId, toast]);

  const handleAnswer = async (answer) => {
    setAnswers({ ...answers, [currentQuestion]: answer });
    
    try {
      const response = await axios.post(`${API_URL}/api/quiz/submit`, {
        weekNumber: parseInt(week),
        subtopicId,
        question: questions[currentQuestion],
        answer,
      });
      
      // Show feedback
      toast({
        title: response.data.isCorrect ? 'Correct!' : 'Incorrect',
        description: response.data.feedback.explanation,
        status: response.data.isCorrect ? 'success' : 'error',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to submit answer',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Quiz completed
      navigate(`/topic/${week}/${subtopicId}`);
    }
  };

  if (loading) {
    return <Text>Loading questions...</Text>;
  }

  const question = questions[currentQuestion];

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <Progress value={(currentQuestion / questions.length) * 100} />
          <Text mt={2}>
            Question {currentQuestion + 1} of {questions.length}
          </Text>
        </Box>

        <Card>
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Heading size="md">{question?.question}</Heading>

              {question?.type === 'mcq' && (
                <RadioGroup
                  onChange={(value) => handleAnswer(value)}
                  value={answers[currentQuestion]}
                >
                  <VStack align="stretch">
                    {Object.entries(question.options).map(([key, value]) => (
                      <Radio key={key} value={key}>
                        {key}: {value}
                      </Radio>
                    ))}
                  </VStack>
                </RadioGroup>
              )}

              {question?.type === 'true_false' && (
                <RadioGroup
                  onChange={(value) => handleAnswer(value)}
                  value={answers[currentQuestion]}
                >
                  <VStack align="stretch">
                    <Radio value="true">True</Radio>
                    <Radio value="false">False</Radio>
                  </VStack>
                </RadioGroup>
              )}

              {question?.type?.toLowerCase() === 'short_answer' && (
                <Input
                  placeholder="Type your answer"
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => handleAnswer(e.target.value)}
                />
              )}
            </VStack>
          </CardBody>
          <CardFooter>
            <Button
              colorScheme="blue"
              onClick={handleNext}
              isDisabled={!answers[currentQuestion]}
            >
              {currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}
            </Button>
          </CardFooter>
        </Card>
      </VStack>
    </Box>
  );
}

export default Quiz; 